import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/utility/usePageTitle";
import UserManager from "./UserManager";
import AddUserDialog from "./AddUserDialog";
import EditUserDialog from "./EditUserDialog";
import EditSiteDialog from "./EditSiteDialog";
import {
  useGetUsers,
  useCreateUser,
  usePartialUpdateUser,
  useDeleteUser,
  useGetSites,
  useCreateSite,
} from "../../hooks";
import { useUpdateSite } from "../../hooks/site/useUpdateSite";
import type { UpdateSiteRequest } from "../../hooks/site/useUpdateSite";
import type { Site, SiteGroup, UserData, NewUser } from "../../types/organisation";
import type {
  User as BackendUser,
  CreateUserRequest,
  PartialUpdateUserRequest,
} from "../../types/user";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import { useGetActivites } from "../../hooks/reference/useGetActivites";
import { useGetFilieres } from "../../hooks/reference/useGetFilieres";
import { useGetRegions } from "../../hooks/reference/useGetRegions";
import { useGetSocietes } from "../../hooks/reference/useGetSocietes";
import "./OrganisationPage.css";
import { Building2, Plus, Users, Settings } from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { ConfigDialog } from "Components/ConfigDialog/ConfigDialog";
import { EntityManager } from "Components/EntityManager";
import { ExpandablePanel } from "Components/ExpandablePanel";
import AddSiteGroupDialog from "./AddSiteGroupDialog";
import { useDeleteSite } from "../../hooks/site/useDeleteSite";

interface OrganisationPageProps {
  user: BackendUser; // current logged-in user (do not shadow with other vars)
}

const OrganisationPage = ({ user: currentUser }: OrganisationPageProps) => {
  const navigate = useNavigate();
  const handleLogout = () => navigate("/");
  const pageTitle = usePageTitle();

  // Panel expansion states
  const [expandedPanel, setExpandedPanel] = useState<"org" | "users" | null>(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);

  // Sites (still local/static for now — you can fetch sites similarly later)
  const { data: sites = [] } = useGetSites();
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  // keep deleteSiteGroup if you still delete groups elsewhere
  const deleteSiteGroup = useDeleteSite();

  // Fetch reference data for resolving site foreign keys
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();
  const { data: societes = [] } = useGetSocietes();

  const topbarProps = {
    title: pageTitle,
    userName: (currentUser && (currentUser.first_name || currentUser.username)) || "User",
    onLogout: handleLogout,
    user: currentUser,
    sites: sites,
  };

  // keep local newGroup state for dialog inputs
  const [newGroup, setNewGroup] = useState<SiteGroup>({
    name: "",
    description: "",
    type: "Interne",
    siteId: 0,
    members: [],
  });
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);

  // --- USERS: now driven by backend hooks (no local hardcoded users) ---
  const { data: backendUsers = [] } = useGetUsers();
  const createUser = useCreateUser();
  const partialUpdateUser = usePartialUpdateUser();
  const deleteUser = useDeleteUser();

  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditSiteDialogOpen, setIsEditSiteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // UI user form state (for Add)
  const [newUser, setNewUser] = useState<NewUser>(() => ({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    site: "",
    role: "user",
  }));

  // Currently edited user in UI shape
  const [userBeingEdited, setUserBeingEdited] = useState<UserData | null>(null);

  // ROLE mapping between UI labels and backend values
  const uiRoleToBackend = (r: string) => {
    switch (r) {
      case "Admin":
        return "admin";
      case "Super User":
        return "superuser";
      case "Agent de saisie":
        return "agent";
      default:
        return "user";
    }
  };
  const backendRoleToUI = (r: string) => {
    switch (r) {
      case "admin":
        return "Admin";
      case "superuser":
        return "Super User";
      case "agent":
        return "Agent de saisie";
      default:
        return "User";
    }
  };

  // Helpers to map site name/id <-> id/name
  const siteIdToName = useCallback(
    (id?: number) => sites.find((s) => s.id === id)?.name ?? "",
    [sites]
  );

  // Convert backend user to UI user shape
  const backendToUI = useCallback(
    (u: BackendUser): UserData => {
      const mapped = {
        id: u.id,
        username: u.username,
        firstName: (u.first_name as string) || "",
        lastName: (u.last_name as string) || "",
        email: u.email || "",
        site: siteIdToName(u.sites?.[0]), // assume single-site users; show site name or empty
        sites: u.sites || [], // ✅ Map the full sites array
        role: backendRoleToUI(u.role),
        password: undefined,
      };

      return mapped;
    },
    [siteIdToName]
  );

  // Derived list for UI components
  const uiUsers = useMemo(() => backendUsers.map(backendToUI), [backendUsers, backendToUI]);

  // Filter users based on search (can reuse existing UserManager filtering but keep this for other uses)
  const filteredUsers = uiUsers.filter((u) =>
    [u.username, u.firstName, u.lastName, u.email, u.site, u.role]
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  // ---- Handlers that call backend ----

  // Helper function to get clean user form state
  const getCleanUserState = (): NewUser => ({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    site: "",
    role: "user",
  });

  // Helper function to check if user can manage other users
  const canManageUsers = currentUser?.role === "admin" || currentUser?.role === "superuser";

  // Debug function to check authentication and user info
  const debugUserInfo = () => {
    console.log("🔍 [DEBUG] Current User Info:", {
      currentUser,
      role: currentUser?.role,
      canManageUsers,
      authToken: localStorage.getItem("authToken"),
      accessToken: localStorage.getItem("ACCESS_TOKEN"),
      refreshToken: localStorage.getItem("refreshToken"),
    });
  };

  // Add debug info when component mounts or currentUser changes
  React.useEffect(() => {
    debugUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, canManageUsers]);

  const handleOpenAddUserDialog = () => {
    // Check permissions before opening dialog
    if (!canManageUsers) {
      alert(
        "❌ Permission denied: Only Admins and Superusers can create users. Current user role: " +
          currentUser?.role
      );
      return;
    }

    // Reset form to clean state when opening the dialog
    setNewUser(getCleanUserState());
    setIsAddDialogOpen(true);
  };

  const handleAddUser = async (payload: CreateUserRequest) => {
    createUser.mutate(payload, {
      onSuccess: () => {
        setNewUser(getCleanUserState());
        setIsAddDialogOpen(false);
      },
      onError: (err) => {
        console.error("Failed to create user:", err);
        // Show more detailed error message
        if (err?.message?.includes("permission")) {
          alert(
            "❌ Permission denied: Only Admins and Superusers can create users. Current user role: " +
              currentUser?.role
          );
        } else {
          alert("❌ Failed to create user: " + (err?.message || "Unknown error"));
        }
      },
    });
  };
  const handleEditUser = (u: UserData) => {
    // Check permissions before opening dialog
    if (!canManageUsers) {
      alert(
        "❌ Permission denied: Only Admins and Superusers can edit users. Current user role: " +
          currentUser?.role
      );
      return;
    }

    // open edit dialog with a cloned UI user object
    setUserBeingEdited({ ...u });
    setIsEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userBeingEdited) return;

    console.log("🔍 [handleSaveUser] Starting user update:", {
      userBeingEdited,
      currentUserRole: currentUser?.role,
      canManageUsers,
    });

    // Use the sites array directly instead of converting from site string
    const sitesArray =
      userBeingEdited.sites && userBeingEdited.sites.length > 0 ? userBeingEdited.sites : [];

    const payload: PartialUpdateUserRequest = {
      username: userBeingEdited.username,
      role: uiRoleToBackend(userBeingEdited.role),
      sites: sitesArray, // Use the full sites array
      email: (userBeingEdited.email ?? "").toString().trim() || "",
      first_name: (userBeingEdited.firstName ?? "").toString().trim() || "",
      last_name: (userBeingEdited.lastName ?? "").toString().trim() || "",
    };

    console.log("🔍 [handleSaveUser] Payload:", payload);
    console.log("🔍 [handleSaveUser] Auth token:", localStorage.getItem("authToken"));

    partialUpdateUser.mutate(
      { userId: userBeingEdited.id, userData: payload },
      {
        onSuccess: () => {
          console.log("✅ [handleSaveUser] User updated successfully");
          setUserBeingEdited(null);
          setIsEditDialogOpen(false);
        },
        onError: (err) => {
          console.error("❌ [handleSaveUser] Failed to update user:", err);
          console.error("❌ [handleSaveUser] Error details:", {
            message: err?.message,
            error: err,
          });
          // Show more detailed error message
          if (err?.message?.includes("permission")) {
            alert(
              "❌ Permission denied: Only Admins and Superusers can edit users. Current user role: " +
                currentUser?.role
            );
          } else {
            alert("❌ Failed to update user: " + (err?.message || "Unknown error"));
          }
        },
      }
    );
  };

  const handleDeleteUser = async (userId: number) => {
    // Find the user in our local state to verify
    const userToDelete = uiUsers.find((u) => u.id === userId);
    if (!userToDelete) {
      alert("User not found in local data!");
      return;
    }

    if (window.confirm(`Are you sure you want to delete user "${userToDelete.username}"?`)) {
      try {
        await deleteUser.mutateAsync(userId);
        alert("User deleted successfully!");
      } catch (error: any) {
        // ✅ Show specific error message to user
        if (error.message?.includes("Authentication")) {
          alert("❌ Authentication error: " + error.message);
        } else if (error.message?.includes("permission")) {
          alert("❌ Permission error: " + error.message);
        } else {
          alert("❌ Failed to delete user: " + (error.message || "Unknown error"));
        }
      }
    }
  };

  // Create an actual Site in backend when dialog saved.
  const handleAddGroup = () => {
    if (!newGroup.name) {
      alert("Please enter a site name");
      return;
    }

    // Validate required entity fields
    const societe = (newGroup as any).societe;
    const bu = (newGroup as any).bu;
    const activite = (newGroup as any).activite;
    const filiere = (newGroup as any).filiere;
    const region = (newGroup as any).region;

    if (!societe || !bu || !activite || !filiere || !region) {
      alert("Please select all required entity fields (Société, BU, Activité, Filière, Région)");
      return;
    }

    const payload = {
      name: newGroup.name,
      require_double_validation: (newGroup as any).requireDoubleValidation ?? false,
      config_json: {},
      societe,
      bu,
      activite,
      filiere,
      region,
    };

    createSite.mutate(payload, {
      onSuccess: () => {
        setNewGroup({
          name: "",
          description: "",
          type: "Interne",
          siteId: 0,
          members: [],
        });
        setIsAddGroupOpen(false);
      },
      onError: (err: any) => {
        console.error("Create site failed (mutation error):", err);
        // err is an Error thrown in hook => show message
        alert("Failed to create site: " + (err?.message ?? JSON.stringify(err)));
      },
    });
  };

  const handleDeleteGroup = async (id: number) => {
    // Find the site to show proper confirmation
    const siteToDelete = sites.find((s) => s.id === id);
    const siteName = siteToDelete?.name || `Site ${id}`;

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${siteName}" ?`)) return;

    try {
      console.log("🚀 Calling deleteSiteGroup.mutateAsync with:", id);
      await deleteSiteGroup.mutateAsync(id);
      alert(`Site "${siteName}" supprimé avec succès!`);
    } catch (error: any) {
      console.error("Error deleting site:", error);

      // ✅ Show specific error message to user
      if (error.message?.includes("Authentication")) {
        alert("❌ Authentication error: " + error.message);
      } else if (error.message?.includes("permission")) {
        alert("❌ Permission error: " + error.message);
      } else {
        alert("❌ Failed to delete site: " + (error.message || "Unknown error"));
      }
    }
  };

  const handleEditSite = (site: Site) => {
    setSelectedSite(site);
    setIsEditSiteDialogOpen(true);
  };

  const handleUpdateSite = async (id: number, data: UpdateSiteRequest) => {
    try {
      await updateSite.mutateAsync({ id, data });
      setIsEditSiteDialogOpen(false);
      setSelectedSite(null);
      console.log("Site updated successfully");
    } catch (error) {
      console.error("Failed to update site:", error);
      alert("Failed to update site");
    }
  };

  return (
    <div className="dashboard-content">
      <Topbar {...topbarProps} />
      <main className="main-dashboard">
        <div className="organisation-page">
          <div className="page-header">
            <div>
              <p>Gestion des unités organisationnelles et des utilisateurs</p>
            </div>
            <button
              className="config-button"
              onClick={() => setIsConfigDialogOpen(true)}
              title="Configuration des sites"
            >
              <Settings size={20} />
              Configuration
            </button>
          </div>

          {/* User Management Panel */}
          <ExpandablePanel
            id="users"
            title="Gestion d'Utilisateur"
            description="Administration des comptes utilisateurs et permissions"
            icon={
              <span className="icon">
                <Users size={30} color="#6a0dad" />
              </span>
            }
            color="purple"
            metricValue={uiUsers.length}
            metricLabel="Utilisateurs actifs"
            expandedPanel={expandedPanel as string | null}
            setExpandedPanel={setExpandedPanel as (panel: string | null) => void}
          >
            <UserManager
              users={filteredUsers}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onAdd={handleOpenAddUserDialog}
              onDelete={handleDeleteUser}
              onEdit={handleEditUser}
              sites={sites}
              canManageUsers={canManageUsers}
            />

            {/* ADD USER DIALOG */}
            {isAddDialogOpen && (
              <AddUserDialog
                isOpen={isAddDialogOpen}
                newUser={newUser}
                setNewUser={setNewUser}
                sites={sites as Site[]}
                onSave={handleAddUser}
                onClose={() => {
                  setIsAddDialogOpen(false);
                  // Reset form state when dialog is closed
                  setNewUser(getCleanUserState());
                }}
              />
            )}

            {/* EDIT USER DIALOG */}
            {isEditDialogOpen && userBeingEdited && (
              <EditUserDialog
                isOpen={isEditDialogOpen}
                user={userBeingEdited}
                setUser={(updated) => setUserBeingEdited(updated)}
                sites={sites as Site[]}
                onSave={handleSaveUser}
                onClose={() => {
                  setIsEditDialogOpen(false);
                  setUserBeingEdited(null);
                }}
              />
            )}
          </ExpandablePanel>

          {/* Organisation Panel */}
          <ExpandablePanel
            id="org"
            title="Sites"
            description="Gestion des sites"
            icon={
              <span className="icon">
                <Building2 size={30} color="#0024a4ff" />
              </span>
            }
            color="blue"
            metricValue={sites.length}
            metricLabel="Unités totales"
            expandedPanel={expandedPanel as string | null}
            setExpandedPanel={setExpandedPanel as (panel: string | null) => void}
          >
            <div className="single-panel">
              <EntityManager
                title="Sites"
                fields={[
                  {
                    key: "name" as any,
                    label: "Nom",
                    placeholder: "Nom du site",
                  },
                  {
                    key: "societe" as any,
                    label: "Société",
                    placeholder: "Société",
                  },
                  {
                    key: "bu" as any,
                    label: "Business Unit",
                    placeholder: "BU",
                  },
                  {
                    key: "activite" as any,
                    label: "Activité",
                    placeholder: "Activité",
                  },
                  {
                    key: "filiere" as any,
                    label: "Filière",
                    placeholder: "Filière",
                  },
                  {
                    key: "region" as any,
                    label: "Région",
                    placeholder: "Région",
                  },
                  {
                    key: "require_double_validation" as any,
                    label: "Double Validation",
                    placeholder: "true/false",
                  },
                ]}
                data={sites as any}
                onEdit={handleEditSite}
                onDelete={(site: any) => {
                  handleDeleteGroup(site.id);
                }}
                extraActionButton={
                  <button className="btn-primary" onClick={() => setIsAddGroupOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                  </button>
                }
                formatField={(k: any, v: any) => {
                  if (k === "require_double_validation") return v ? "Oui" : "Non";
                  if (k === "societe") {
                    const s = societes.find((x: any) => x.id === v);
                    return s ? s.name : String(v || "—");
                  }
                  if (k === "bu") {
                    const b = bus.find((x: any) => x.id === v);
                    return b ? `${b.code} - ${b.name}` : String(v || "—");
                  }
                  if (k === "activite") {
                    const a = activites.find((x: any) => x.id === v);
                    return a ? `${a.code} - ${a.name}` : String(v || "—");
                  }
                  if (k === "filiere") {
                    const f = filieres.find((x: any) => x.id === v);
                    return f ? `${f.code} - ${f.name}` : String(v || "—");
                  }
                  if (k === "region") {
                    const r = regions.find((x: any) => x.id === v);
                    return r ? `${r.code} - ${r.name}` : String(v || "—");
                  }
                  return String(v ?? "—");
                }}
              />

              {/* Edit Site Dialog */}
              <EditSiteDialog
                isOpen={isEditSiteDialogOpen}
                site={selectedSite}
                onSave={handleUpdateSite}
                onClose={() => {
                  setIsEditSiteDialogOpen(false);
                  setSelectedSite(null);
                }}
              />

              {isAddGroupOpen && (
                <AddSiteGroupDialog
                  isOpen={isAddGroupOpen}
                  newGroup={newGroup}
                  setNewGroup={setNewGroup}
                  onAddGroup={handleAddGroup}
                  onClose={() => setIsAddGroupOpen(false)}
                />
              )}
            </div>
          </ExpandablePanel>
        </div>
        {/* CONFIGURATION DIALOG */}
        <ConfigDialog isOpen={isConfigDialogOpen} onClose={() => setIsConfigDialogOpen(false)} />
      </main>
    </div>
  );
};

export default OrganisationPage;
